import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { toast } from "sonner";

import ProjectAPI from "../../api/ProjectAPI";

import type { UseMutateFunction } from "@tanstack/react-query";

import type {
  CountryGroup,
  CountryGroupPayload,
  CreateCountryGroupResponse,
} from "../../api/ProjectAPI.types";

interface UpdateCountryGroupVariables {
  uuid: string;
  data: CountryGroupPayload;
}

export interface UseCountryGroupMutationsResult {
  createCountryGroup: (
    data: CountryGroupPayload,
  ) => Promise<CreateCountryGroupResponse>;
  updateCountryGroup: (
    variables: UpdateCountryGroupVariables,
  ) => Promise<CountryGroup>;
  deleteCountryGroup: UseMutateFunction<void, unknown, string>;
  isSaving: boolean;
  isDeleting: boolean;
}

export function useCountryGroupMutations(): UseCountryGroupMutationsResult {
  const queryClient = useQueryClient();

  const getCountryGroupListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getCountryGroupList,
    params: [],
  });

  const invalidateList = (): void => {
    queryClient.invalidateQueries(getCountryGroupListQuery.queryKey);
  };

  // Create/update surface validation errors (e.g. label already taken) to the
  // caller so it can map them onto the form field — the toast is left to the
  // form. Delete has no form, so it toasts here.
  const createMutation = useMutation({
    mutationFn: async (data: CountryGroupPayload) =>
      ProjectAPI.createCountryGroup({ data }),
    onSuccess: () => {
      toast.success(t("editor-notif:saveSuccess"));
      invalidateList();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ uuid, data }: UpdateCountryGroupVariables) =>
      ProjectAPI.updateCountryGroup({ uuid, data }),
    onSuccess: () => {
      toast.success(t("editor-notif:saveSuccess"));
      invalidateList();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (uuid: string) => ProjectAPI.deleteCountryGroup({ uuid }),
    onSuccess: () => {
      toast.success(t("editor-notif:deleteSuccess"));
      invalidateList();
    },
    onError: () => {
      toast.error(t("editor-notif:deleteError"));
    },
  });

  return {
    createCountryGroup: async (data: CountryGroupPayload) =>
      createMutation.mutateAsync(data),
    updateCountryGroup: async (variables: UpdateCountryGroupVariables) =>
      updateMutation.mutateAsync(variables),
    deleteCountryGroup: deleteMutation.mutate,
    isSaving: createMutation.isLoading || updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
}
